import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type CompradorListado, type ListadoVentas, type Unidad, type VentaDetalle } from "@/lib/api";
import {
  aPayloadVenta, filtrosVentasIniciales, formVenta, opcionesUnidadesDisponibles, queryVentas, validarVenta, type ErroresVenta, type FiltrosVentas, type FormVenta, type PayloadVenta,
} from "@/lib/ventas";

const SIN_ERRORES: ErroresVenta = {};

/** Lógica del listado de ventas (sin JSX). Admin crea; los demás roles miran (el fiduciante con el comprador recortado). */
export function useVentasTab() {
  const { fid, membresia } = useContextoApp();
  const navegar = useNavigate();
  const base = `/api/fideicomisos/${fid}/ventas`;
  const esAdmin = membresia.rol === "admin";

  const [filtros, setFiltros] = useState<FiltrosVentas>(filtrosVentasIniciales);
  const listado = useApiResource<ListadoVentas>(`${base}${queryVentas(filtros)}`);
  const compradores = useApiResource<CompradorListado[]>(`/api/fideicomisos/${fid}/compradores`);
  const [abierto, setAbierto] = useState(false);
  const unidades = useApiResource<Unidad[]>(esAdmin && abierto ? `/api/fideicomisos/${fid}/unidades` : null);

  const [form, setForm] = useState<FormVenta>(() => formVenta());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((datos: PayloadVenta) => api.post<VentaDetalle>(base, datos));
  const errores = validarVenta(form);

  const abrirNuevo = () => {
    setForm(formVenta());
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setAbierto(true);
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    const creada = await accionGuardar.run(aPayloadVenta(form));
    if (creada) {
      setAbierto(false);
      void navegar(`/ventas/${creada.id}`);
    }
  };

  return {
    esAdmin,
    items: listado.data?.items ?? [],
    totales: listado.data?.totales ?? null,
    cargando: listado.loading && !listado.data,
    errorCarga: listado.data ? null : (listado.error?.message ?? null),
    reintentar: listado.recargar,
    filtros,
    cambiarFiltro: <K extends keyof FiltrosVentas>(campo: K, valor: FiltrosVentas[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    limpiarFiltros: () => setFiltros(filtrosVentasIniciales()),
    hayFiltros: queryVentas(filtros) !== "",
    compradores: compradores.data ?? [],
    unidadesDisponibles: opcionesUnidadesDisponibles(unidades.data ?? []),
    abierto,
    cerrar: () => setAbierto(false),
    abrirNuevo,
    form,
    cambiar: <K extends keyof FormVenta>(campo: K, valor: FormVenta[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    verFicha: (id: number) => void navegar(`/ventas/${id}`),
  };
}
